import React, { useEffect, useState } from 'react';
import { Mail, Send, Loader2, Calendar, CheckCircle2 } from 'lucide-react';
import { reportsApi } from '../api';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const fmtDT = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d)) return '—';
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function FulfilmentDigestPanel({ theme, isSuper }) {
  const [cfg, setCfg] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');

  const load = async () => {
    try {
      const [c, p] = await Promise.all([reportsApi.digestConfig(), reportsApi.digestPreview()]);
      setCfg(c); setPreview(p);
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Failed to load digest config');
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    if (!isSuper) return;
    setSaving(true); setMsg('');
    try {
      const next = await reportsApi.setDigestConfig(patch);
      setCfg(next);
      // Refresh preview because the threshold may have changed
      const p = await reportsApi.digestPreview();
      setPreview(p);
      setMsg('Saved');
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const sendNow = async () => {
    setSending(true); setMsg('');
    try {
      const r = await reportsApi.sendDigest();
      if (r.ok) {
        setMsg(`Digest queued for ${r.recipientCount} recipient(s) · ${r.slowCount} slow branch(es)`);
        await load();
      } else {
        setMsg(r.reason || 'Nothing sent');
      }
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Send failed');
    } finally { setSending(false); setTimeout(() => setMsg(''), 4000); }
  };

  if (!cfg) return null;

  return (
    <div className="fm-card p-5 mt-6" data-testid="digest-panel">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Mail size={16} style={{ color: theme.primary }} />
        <h3 className="text-[15px] font-semibold text-[#232323]">Weekly fulfilment digest</h3>
        <span className="text-[12px] text-[#7a6f5c] ml-auto">
          Last sent: <span className="font-semibold text-[#232323]" data-testid="digest-last-sent">{fmtDT(cfg.lastSentAt)}</span>
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div>
          <label className="text-[11.5px] text-[#7a6f5c] block mb-1">Auto-send</label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="digest-enabled-toggle"
              checked={!!cfg.enabled}
              disabled={!isSuper || saving}
              onChange={e => save({ enabled: e.target.checked })}
              className="w-4 h-4"
              style={{ accentColor: theme.primary }}
            />
            <span className="text-[13px]">{cfg.enabled ? 'ON — weekly on selected day' : 'OFF'}</span>
          </label>
        </div>
        <div>
          <label className="text-[11.5px] text-[#7a6f5c] block mb-1">SLA threshold (days)</label>
          <input
            type="number"
            min={1}
            max={30}
            data-testid="digest-threshold-input"
            defaultValue={cfg.thresholdDays}
            disabled={!isSuper || saving}
            onBlur={e => {
              const v = Number(e.target.value);
              if (v !== cfg.thresholdDays) save({ thresholdDays: v });
            }}
            className="fm-focus w-full border border-[#E7E2D3] rounded-md px-2 py-1.5 text-[13px] bg-white"
          />
        </div>
        <div>
          <label className="text-[11.5px] text-[#7a6f5c] block mb-1">Day of week</label>
          <select
            data-testid="digest-day-select"
            value={cfg.dayOfWeek}
            disabled={!isSuper || saving}
            onChange={e => save({ dayOfWeek: Number(e.target.value) })}
            className="fm-select fm-focus w-full border border-[#E7E2D3] rounded-md px-2 py-1.5 text-[13px] bg-white"
          >
            {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          <button
            onClick={sendNow}
            disabled={sending}
            data-testid="digest-send-now-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-white text-[13px] font-medium disabled:opacity-50"
            style={{ background: theme.primary }}
          >
            {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            Send digest now
          </button>
        </div>
      </div>

      {preview && (
        <div className="border border-[#E7E2D3] rounded-md p-3 bg-[#FAF7EE]" data-testid="digest-preview">
          <div className="flex items-center gap-2 mb-2 text-[12.5px] text-[#7a6f5c]">
            <Calendar size={12} />
            <span>Preview — <span className="font-semibold text-[#232323]" data-testid="digest-slow-count">{preview.slow?.length || 0}</span> branch(es) exceed {preview.threshold} days ·
              sending to <span className="font-semibold text-[#232323]" data-testid="digest-recipient-count">{preview.recipients?.length || 0}</span> recipient(s)</span>
          </div>
          {preview.slow && preview.slow.length > 0 ? (
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-left text-[11px] text-[#7a6f5c]">
                  <th className="px-2 py-1 font-medium">Branch</th>
                  <th className="px-2 py-1 font-medium text-center">Fulfilled requisitions (last 30d)</th>
                  <th className="px-2 py-1 font-medium text-right">Avg. days</th>
                </tr>
              </thead>
              <tbody>
                {preview.slow.map(b => (
                  <tr key={b.branch} className="border-t border-[#EFEADC]" data-testid={`digest-branch-${b.branch}`}>
                    <td className="px-2 py-1 font-mono">{b.branch}</td>
                    <td className="px-2 py-1 text-center">{b.reqs}</td>
                    <td className="px-2 py-1 text-right font-semibold text-[#B84A4A]">{b.avgDays} d</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex items-center gap-1.5 text-[12.5px] text-[#2f6b41] py-1">
              <CheckCircle2 size={13} /> No branch exceeds the threshold — the digest email would confirm all branches are on track.
            </div>
          )}
        </div>
      )}

      {msg && <div className="mt-3 text-[12px] text-[#7a6f5c]" data-testid="digest-msg">{msg}</div>}
      {!isSuper && (
        <div className="mt-3 text-[11.5px] text-[#7a6f5c]">
          Only Super Admin can change these settings — you can trigger a send manually.
        </div>
      )}
    </div>
  );
}
